import { useUserData } from './useUserData';
import { useState } from 'react';
import {
  ANSWER_MINIMAL_LENGTH,
  SKIPPED_ANSWER_VALUE,
} from '../constants/common';
import { fetchChatGPTSimple, mapWithConcurrency } from '../utils/book';
import { useTranslation } from 'react-i18next';

// Max number of concurrent n8n webhook requests. The webhook serializes work,
// so firing all answers at once made later ones wait past the ~125s Cloudflare
// timeout (524). Keeping a small number in flight keeps every request fast.
const REWRITE_CONCURRENCY = 15;

export const useBookData = (
  user,
  onEnd,
  minGenNum = 1,
  maxGenNum = 50,
  model
) => {
  const { userData, userQuestionsWithAnswers } = useUserData(user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({});
  const { i18n } = useTranslation();

  // Critical safety check: Ensure we have valid user context
  const userEmail = user?.email;
  const userUid = user?.uid;

  const bookQaData = userData?.book_qa_data;

  const language = userData?.language || i18n.language;

  const modifiedUserQuestions =
    userEmail && userQuestionsWithAnswers
      ? userQuestionsWithAnswers
          ?.filter((q) => {
            if (
              !q ||
              !q.answers ||
              !q.answers[0] ||
              !q.answers[0]['stripped-text']
            ) {
              return true;
            }

            return q.answers[0]['stripped-text'] !== SKIPPED_ANSWER_VALUE;
          })
          ?.filter(
            (q) =>
              minGenNum <= Number(q?.questionId) &&
              Number(q?.questionId) <= maxGenNum
          )
          ?.map((item) => {
            let singleTextAnswer = '';

            item?.answers?.forEach((a) => {
              // Additional safety check: Verify answer belongs to current user
              if (a?.sender && a.sender !== userEmail) {
                alert(
                  `useBookData: CRITICAL - Found answer from different user! Expected user: ${userEmail} Found user: ${a.sender} Question ID: ${item?.questionId}`
                );
                return;
              }

              if (a?.['stripped-text'] && a?.['stripped-text'] !== null) {
                singleTextAnswer += a?.['stripped-text'];
              }
            });

            const questionAsTitle =
              Object.values(bookQaData || {})?.find(
                (q) => Number(q?.questionId) === Number(item?.questionId)
              )?.customTitle || item?.questionAsTitle;

            return {
              questionId: item?.questionId,
              question: item?.question,
              questionAsTitle,
              answer: singleTextAnswer,
              photos: item?.photos,
            };
          })
      : [];

  const generateChapters = async () => {
    // Critical safety check before generating chapters
    if (!userEmail || !userUid) {
      // setError('Invalid user context for book generation');
      alert(`generateChapters: Missing user context ${userEmail} ${userUid}`);
      // return;
    }

    if (!modifiedUserQuestions || modifiedUserQuestions.length === 0) {
      // setError('No valid user questions found for book generation');
      alert(`generateChapters: No valid questions for user ${userEmail}`);
      // return;
    }

    try {
      setLoading(true);
      const systemPromptAdditions = {
        en: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        en_US: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-US. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-US. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        'en-US': {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-US. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-US. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        en_GB: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-GB. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-GB. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        'en-GB': {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-GB. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-GB. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        en_AU: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-AU. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-AU. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        'en-AU': {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-AU. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-AU. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        en_NZ: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-NZ. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-NZ. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        'en-NZ': {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-NZ. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-NZ. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        en_CA: {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-CA. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-CA. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },
        'en-CA': {
          48: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-CA. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Thirdly, highlight the book title from the text with <b> html tag. Return only TEXT.`,
          other: `Rewrite TEXT. Firstly, proofread text by fixing grammar and syntax in en-CA. Secondly, split this text into paragraphs and format paragraphs with html <p> tags. Return only TEXT.`,
        },

        es: {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis. En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis. En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        es_ES: {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-ES). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-ES). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        'es-ES': {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-ES). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-ES). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        es_US: {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-US). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-US). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        'es-US': {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-US). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-US). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        es_MX: {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-MX). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-MX). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },
        'es-MX': {
          48: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-MX). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. En tercer lugar, resalta el título del libro en el texto con la etiqueta HTML <b>. Devuelve solo el TEXTO.`,
          other: `Reescribe el TEXTO. En primer lugar, corrige el texto revisando la gramática y la sintaxis (en es-MX). En segundo lugar, divide este texto en párrafos y formatea los párrafos con etiquetas HTML <p>. Devuelve solo el TEXTO.`,
        },

        fr: {
          48: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe. Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Enfin, mets en évidence le titre du livre dans le texte avec la balise HTML <b>. Ne renvoie que le TEXTE.`,
          other: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe. Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Ne renvoie que le TEXTE.`,
        },
        fr_FR: {
          48: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-FR). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Enfin, mets en évidence le titre du livre dans le texte avec la balise HTML <b>. Ne renvoie que le TEXTE.`,
          other: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-FR). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Ne renvoie que le TEXTE.`,
        },
        'fr-FR': {
          48: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-FR). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Enfin, mets en évidence le titre du livre dans le texte avec la balise HTML <b>. Ne renvoie que le TEXTE.`,
          other: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-FR). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Ne renvoie que le TEXTE.`,
        },
        fr_CA: {
          48: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-CA). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Enfin, mets en évidence le titre du livre dans le texte avec la balise HTML <b>. Ne renvoie que le TEXTE.`,
          other: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-CA). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Ne renvoie que le TEXTE.`,
        },
        'fr-CA': {
          48: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-CA). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Enfin, mets en évidence le titre du livre dans le texte avec la balise HTML <b>. Ne renvoie que le TEXTE.`,
          other: `Réécris le TEXTE. Tout d'abord, corrige le texte en vérifiant la grammaire et la syntaxe (en fr-CA). Ensuite, divise ce texte en paragraphes et formate les paragraphes avec des balises HTML <p>. Ne renvoie que le TEXTE.`,
        },

        de: {
          48: `Schreibe den TEXT neu. Zuerst korrigiere den Text, indem du Grammatik und Syntax überprüfst. Zweitens teile diesen Text in Absätze und formatiere die Absätze mit HTML <p>-Tags. Drittens hebe den Titel des Buches im Text mit dem HTML-Tag <b> hervor. Gib nur den TEXT zurück.`,
          other: `Schreibe den TEXT neu. Zuerst korrigiere den Text, indem du Grammatik und Syntax überprüfst. Zweitens teile diesen Text in Absätze und formatiere die Absätze mit HTML <p>-Tags. Gib nur den TEXT zurück.`,
        },
        it: {
          48: `Riscrivi il TESTO. In primo luogo, correggi il testo controllando la grammatica e la sintassi. In secondo luogo, dividi questo testo in paragrafi e formatta i paragrafi con i tag HTML <p>. In terzo luogo, evidenzia il titolo del libro nel testo con il tag HTML <b>. Restituisci solo il TESTO.`,
          other: `Riscrivi il TESTO. In primo luogo, correggi il testo controllando la grammatica e la sintassi. In secondo luogo, dividi questo testo in paragrafi e formatta i paragrafi con i tag HTML <p>. Restituisci solo il TESTO.`,
        },
      };

      // IF book_qa_data is empty, this code below is failing

      const results = await mapWithConcurrency(
        modifiedUserQuestions,
        REWRITE_CONCURRENCY,
        async (item) => {
          if (!item?.answer) return undefined;

          const systemPromptAddition =
            Number(item?.questionId) === 48
              ? systemPromptAdditions?.[language]?.[48]
              : systemPromptAdditions?.[language]?.other;

          return fetchChatGPTSimple({
            answer: item?.answer,
            model,
            language,
            systemPromptAddition,
          });
        }
      );
      const merged = modifiedUserQuestions?.map((item, idx) => ({
        ...item,
        answer:
          item.answer.length >= ANSWER_MINIMAL_LENGTH
            ? results?.[idx]
            : item.answer,
      }));

      setData(merged);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      onEnd();
    }
  };

  const constructBookData = () => {
    // Safety check before constructing book data
    if (!userEmail || !userUid) {
      console.error('constructBookData: Missing user context', {
        userEmail,
        userUid,
      });
      // setError('Invalid user context for book construction');
      // return;
    }

    const merged = modifiedUserQuestions?.map((item) => ({
      ...item,
      answer: bookQaData?.[Number(item?.questionId)]?.text,
    }));
    setData(merged);
  };

  return { generateChapters, constructBookData, loading, error, data };
};
